/**
 * /recalls — 召回库（Phase 4 §4.5）。
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import ChipFilter from '@/components/ChipFilter';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import StatCard from '@/components/StatCard';
import { api } from '@/lib/api';
import { useWeekStore } from '@/stores/weekStore';
import { parseWeekMonday } from '@/lib/week';

interface RecallRow {
  recall_id: string;
  announcement_date: string;
  company: string | null;
  brand_id: string | null;
  model_id: string | null;
  vehicle_count: number;
  defect_description: string | null;
  remedy: string | null;
  is_ota_fix: boolean;
  is_safety_related: boolean;
  source_url: string;
}

const BRAND_OPTIONS = [
  { value: '', label: '全部品牌' },
  { value: 'GAC-AION', label: '广汽埃安' },
  { value: 'GAC-TRUMP', label: '广汽传祺' },
  { value: 'GAC-HYPER', label: '昊铂' },
  { value: 'GAC-HONDA', label: '广汽本田' },
  { value: 'GAC-TOYOTA', label: '广汽丰田' },
];

const SAFETY_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'true', label: '仅安全相关' },
  { value: 'false', label: '非安全相关' },
];

export default function RecallsPage() {
  const { week } = useWeekStore();
  const year = Number(week.split('-W')[0] || new Date().getFullYear());
  const [brand, setBrand] = useState('');
  const [safety, setSafety] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['recalls', year, brand, safety],
    queryFn: async () => {
      const params: Record<string, string | number> = { year, limit: 200 };
      if (brand) params.brand = brand;
      if (safety) params.is_safety_related = safety;
      return (await api.get<RecallRow[]>('/recalls', { params })).data;
    },
    retry: false,
  });

  const recalls = useMemo(() => data ?? [], [data]);
  const usingMock = !data && !isLoading;

  const stats = useMemo(() => {
    const ytdEvents = recalls.length;
    const ytdVehicles = recalls.reduce((s, r) => s + (r.vehicle_count || 0), 0);
    const weekMonday = parseWeekMonday(week);
    const thisWeekCount = weekMonday
      ? recalls.filter((r) => {
          const d = new Date(r.announcement_date);
          const diffDays = (d.getTime() - weekMonday.getTime()) / (1000 * 60 * 60 * 24);
          return diffDays >= 0 && diffDays < 7;
        }).length
      : 0;
    const otaPct =
      ytdEvents === 0 ? 0 : recalls.filter((r) => r.is_ota_fix).length / ytdEvents;
    return { ytdEvents, ytdVehicles, thisWeekCount, otaPct };
  }, [recalls, week]);

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="召回接口暂不可用 — 显示空列表" />}

      <div>
        <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">官方召回库 · {year}</h1>
        <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
          {stats.ytdEvents} 起召回 · 影响 {stats.ytdVehicles.toLocaleString()} 辆
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <StatCard title={`${year} 年召回事件`} value={stats.ytdEvents} hint="YTD 累计" />
        <StatCard
          title="涉及车辆"
          value={stats.ytdVehicles.toLocaleString()}
          hint="YTD 累计"
        />
        <StatCard title="本周新增" value={stats.thisWeekCount} />
        <StatCard
          title="OTA 可修复占比"
          value={`${(stats.otaPct * 100).toFixed(1)}%`}
          hint="召回中可 OTA"
        />
      </div>

      <Panel title="筛选">
        <div className="flex flex-col gap-3">
          <ChipFilter label="品牌" value={brand} onChange={setBrand} options={BRAND_OPTIONS} />
          <ChipFilter label="安全相关" value={safety} onChange={setSafety} options={SAFETY_OPTIONS} />
        </div>
      </Panel>

      <div className="space-y-3">
        {isLoading && <div className="text-[12px] text-slate-500">加载中...</div>}
        {!isLoading && recalls.length === 0 && (
          <div className="text-[12px] text-slate-400 py-12 text-center">暂无召回数据</div>
        )}
        {recalls.map((r) => (
          <RecallCard key={r.recall_id} recall={r} />
        ))}
      </div>
    </div>
  );
}

function RecallCard({ recall }: { recall: RecallRow }) {
  const stripeColor = recall.is_safety_related ? '#DC2626' : '#F59E0B';
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex">
      <div className="w-1.5 shrink-0" style={{ background: stripeColor }} />
      <div className="p-4 flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-semibold text-slate-900">
              {recall.brand_id ?? '—'} · {recall.model_id ?? recall.company ?? '—'}
            </span>
            <span className="text-[11px] text-slate-400">{recall.announcement_date}</span>
          </div>
          <span className="text-[12px] text-slate-600">
            涉及 <b>{recall.vehicle_count.toLocaleString()}</b> 辆
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4 text-[12.5px] text-slate-700">
          <div>
            <div className="text-slate-500 mb-1 text-[11px]">缺陷描述</div>
            <div className="leading-relaxed">{recall.defect_description ?? '—'}</div>
          </div>
          <div>
            <div className="text-slate-500 mb-1 text-[11px]">风险描述</div>
            <div className="leading-relaxed">{recall.is_safety_related ? '安全相关风险' : '一般质量问题'}</div>
          </div>
          <div>
            <div className="text-slate-500 mb-1 text-[11px]">解决措施</div>
            <div className="leading-relaxed">{recall.remedy ?? '—'}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
          {recall.is_safety_related && <Tag bg="#FEE2E2" fg="#991B1B">安全相关</Tag>}
          {recall.is_ota_fix && <Tag bg="#DBEAFE" fg="#1E40AF">可 OTA 修复</Tag>}
          <a
            href={recall.source_url}
            target="_blank"
            rel="noreferrer"
            className="text-sky-600 hover:underline ml-auto"
          >
            查看公告 ↗
          </a>
        </div>
      </div>
    </div>
  );
}

function Tag({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span
      className="px-2 py-0.5 rounded-full font-medium"
      style={{ background: bg, color: fg }}
    >
      {children}
    </span>
  );
}
